import { useCallback, useMemo, useState } from "react";

import { useTableViewPagination } from "@covertree/hooks";
import { ROWS_PER_PAGE } from "@covertree/consts";

import { useGetPendingCancellationPolicyListItemsQuery } from "@hooks/policies/useGetPendingCancellationPolicyListItems";
import {
  PendingCancellationPolicyFilterByStatus,
  PolicyListItemsSortBy,
  SortOrder,
} from "@covertree/agent-portal-graphql";

import { useMappedPendingCancellationPolicies } from "./useMappedPendingCancellationPolicies";

export const filters = [
  {
    label: PendingCancellationPolicyFilterByStatus.All,
    value: PendingCancellationPolicyFilterByStatus.All,
  },
  {
    label: "Non Pay",
    value: PendingCancellationPolicyFilterByStatus.NonPay,
  },
  {
    label: PendingCancellationPolicyFilterByStatus.Inspection,
    value: PendingCancellationPolicyFilterByStatus.Inspection,
  },
  {
    label: "ESign Required",
    value: PendingCancellationPolicyFilterByStatus.EsignRequired,
  },
  {
    label: PendingCancellationPolicyFilterByStatus.Other,
    value: PendingCancellationPolicyFilterByStatus.Other,
  },
];

const initialPagination = {
  from: "0",
  filter: filters[0].value,
  order: SortOrder.Descending,
  sortBy: PolicyListItemsSortBy.CancellationEffectiveTimestamp,
};

const columns = [
  { headerName: "Policy ID" },
  { headerName: "Name" },
  { headerName: "Address" },
  { headerName: "Type" },
  { headerName: "Premium" },
  {
    headerName: "Cancellation Effective Date",
    sortBy: PolicyListItemsSortBy.CancellationEffectiveTimestamp,
  },
  { headerName: "Status" },
  {
    headerName: "Description",
  },
  {
    headerName: "Term",
  },
  { headerName: "Assigned Agent" },
  { headerName: "Assigned Agency" },
  {
    headerName: "Last Activity",
  },
];

export const useGetPendingCancellationPolicies = () => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE);

  const {
    from,
    order,
    sortBy,
    filter,
    currentPage,
    onChangePage,
    onChangeFilter,
    onChangeSort,
  } = useTableViewPagination(0, rowsPerPage, initialPagination);

  const handleSortChange = useCallback(
    (nextSortBy: string) => () => {
      onChangeSort(nextSortBy);
    },
    [onChangeSort]
  );

  const queryVariables = useMemo(
    () => ({
      pagination: {
        from,
        size: rowsPerPage,
      },
      sorting: {
        sort: order,
        sortBy: sortBy as PolicyListItemsSortBy,
      },
      filter: {
        status: filter as PendingCancellationPolicyFilterByStatus,
      },
    }),
    [filter, from, order, sortBy, rowsPerPage]
  );

  const {
    policies: items,
    totalCount,
    loading,
    error,
  } = useGetPendingCancellationPolicyListItemsQuery(queryVariables);

  const policies = useMappedPendingCancellationPolicies(items);

  return {
    policies,
    loading,
    error: !!error,
    columns,
    filters,
    order,
    sortBy,
    currentPage,
    rowsPerPage,
    totalCount,
    handleSortChange,
    handleChangePage: onChangePage,
    handleFilterChange: onChangeFilter,
    handleRowsPerPageChange: setRowsPerPage,
  };
};
